import { Component, ChangeDetectionStrategy, Input, OnInit, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { ShareValuesService } from 'src/app/services/sharedValues/share-values.service';
import { Router } from '@angular/router';

export interface Notification {
  id: number;
  title: string;
  text: string;
  isRead: boolean;
  projectId?: string;
  projectName?: string;
  customerName?: string;
  source?: any;
}

@Component({
  selector: 'app-enhanced-notification',
  templateUrl: './enhanced-notification.component.html',
  styleUrls: ['./enhanced-notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnhancedNotificationComponent implements OnInit, OnDestroy {
  // Input for external notifications
  @Input() inputNotifications: Notification[] = [];
 @Output() closeNotificationPopup = new EventEmitter<void>();
  // Internal state properties
  public notifications: Notification[] = [];
  public isVisible = false;
  public isExpanded = false;
  public slidingOutIds = new Set<number>();
  private notificationSub: any;

  constructor(
    public sharedValue: ShareValuesService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) { }

  public ngOnInit() {
    // Subscribe to notifications$ for real-time updates
    this.notificationSub = this.sharedValue.notifications$.subscribe((serviceNotifications: any[]) => {
      this.notifications = (serviceNotifications || []).map((item: any, index: number) => ({
        id: item.id ? Number(item.id) : index + 1,
        title: item.title || this.getStructuredPayload(item).title || 'Blueprint',
        text: this.getNotificationText(item),
        isRead: item.isRead || item.read || false,
        projectId: this.getProjectId(item),
        projectName: this.getProjectName(item),
        customerName: this.getCustomerName(item),
        source: item
      }));
      this.cdr.markForCheck();
    });
    console.log(this.notificationSub , 'notificationSub in enhanced notification component');
  }

  public ngOnDestroy() {
    if (this.notificationSub) {
      this.notificationSub.unsubscribe();
    }
  }


  // Computed properties as getters
  public get unreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }



  // Public methods to control visibility
  public showPopup() {
    this.isVisible = true;
    this.cdr.markForCheck();
  }

  public togglePopup() {
    this.isVisible = !this.isVisible;
    if (!this.isVisible) {
      this.isExpanded = false;
    }
    this.cdr.markForCheck();
  }

  public closePopup() {
    this.isVisible = false;
    this.isExpanded = false;
    this.cdr.markForCheck();
    this.closeNotificationPopup.emit();
  }

  public markAsRead(notificationId: number) {
    this.notifications = this.notifications.map(n =>
      n.id === notificationId ? { ...n, isRead: true } : n
    );

    // Update the shared service
    this.updateSharedService();
    this.cdr.markForCheck();
  }

  public async onNotificationClick(notification: Notification): Promise<void> {
    this.markAsRead(notification.id);
    const reportProjectId = this.getRoutedRaReportProjectId(notification.text);
    if (!reportProjectId) {
      return;
    }

    const projectId = reportProjectId;
    const queryParams: { [key: string]: string } = {};
    Reflect.set(queryParams, 'project_Id', projectId);
    if (notification.projectName) {
      queryParams.project_Name = notification.projectName;
    }
    if (notification.customerName) {
      queryParams.customer_Name = notification.customerName;
    }

    try {
      await this.router.navigate(['/view-ra-reports'], {
        queryParams,
        onSameUrlNavigation: 'reload'
      });
      if (this.router.url.includes('/view-ra-reports')) {
        this.sharedValue.notifications$.next(this.sharedValue.finalNotification);
      }
    } finally {
      this.closePopup();
    }
  }

  public markAllAsRead() {
    this.notifications = this.notifications.map(n => ({ ...n, isRead: true }));

    // Update the shared service
    this.updateSharedService();
    this.cdr.markForCheck();
  }

  public dismissNotification(event: Event, notificationId: number) {
    event.stopPropagation(); // Prevent triggering markAsRead

    // Add to sliding out set for animation
    this.slidingOutIds.add(notificationId);
    this.cdr.markForCheck();

    // Remove after animation completes
    setTimeout(() => {
      this.notifications = this.notifications.filter(n => n.id !== notificationId);
      this.slidingOutIds.delete(notificationId);

      // Update the shared service
      this.updateSharedService();
      this.cdr.markForCheck();
    }, 300);
  }

  public clearAll() {
    // Add all notification ids to slidingOutIds for animation
    this.notifications.forEach(n => this.slidingOutIds.add(n.id));
    this.cdr.markForCheck();
    setTimeout(() => {
      this.notifications = [];
      this.slidingOutIds.clear();
      // Update the shared service
      this.updateSharedService();
      this.cdr.markForCheck();
      localStorage.removeItem('notificationvalue');
      this.sharedValue.finalNotification = [];
      this.sharedValue.notifications$.next([]);
    }, 300); // match the sliding-out transition duration
  }

  public toggleExpanded() {
    this.isExpanded = !this.isExpanded;
    this.cdr.markForCheck();
  }

  public trackByNotificationId(index: number, notification: Notification): number {
    return notification.id;
  }



  // Public methods for external access
  public addNotification(notification: Omit<Notification, 'id'>) {
    const newId = Math.max(...this.notifications.map(n => n.id), 0) + 1;
    const newNotification: Notification = { ...notification, id: newId };

    this.notifications = [newNotification, ...this.notifications];
    this.updateSharedService();
    this.cdr.markForCheck();
  }

  public getUnreadCount(): number {
    return this.unreadCount;
  }

  public hasNotifications(): boolean {
    return this.notifications.length > 0;
  }

  // Helper method for testing empty state
  public clearAllForTesting(): void {
    this.notifications = [];
    this.updateSharedService();
    this.cdr.markForCheck();
  }

  private getRoutedRaReportProjectId(text: string): string | undefined {
    const normalizedText = String(text || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const reportNotificationPattern = new RegExp(
      '^risk assessment report ra_report_([a-z0-9_-]+)\\.pdf'
      + ' for project ([a-z0-9_-]+) '
      + '(?:is (?:in progress|i?completed)|has been completed)'
      + '[.!]?\\s*(?:\\[[^\\]]*\\])?[.! ]*$',
      'i'
    );
    const match = reportNotificationPattern.exec(normalizedText);
    if (!match || match[1].toLowerCase() !== match[2].toLowerCase()) {
      return undefined;
    }
    return match[2];
  }

  private updateSharedService() {
    // Convert back to shared service format
    const serviceFormat = this.notifications.map(n => {
      const serviceItem: Record<string, unknown> = {
        ...(n.source || {}),
        notification: n.text,
        title: n.title,
        id: n.id,
        read: n.isRead,
        isRead: n.isRead
      };
      if (n.projectId) {
        Reflect.set(serviceItem, 'project_Id', n.projectId);
      }
      return serviceItem;
    });

    this.sharedValue.finalNotification = serviceFormat;
    localStorage.setItem('notificationvalue', JSON.stringify(serviceFormat));
    this.sharedValue.notifications$.next(serviceFormat);
  }

  private getStructuredPayload(item: any): any {
    const notification = item?.notification ?? item?.text ?? item?.message;
    if (notification && typeof notification === 'object') {
      return notification;
    }
    if (typeof notification === 'string') {
      try {
        const parsed = JSON.parse(notification);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch {
        return {};
      }
    }
    return {};
  }

  private getNotificationText(item: any): string {
    const payload = this.getStructuredPayload(item);
    const notification = item?.notification ?? item?.text ?? item?.message;
    const text = payload.notification || payload.text || payload.message
      || (typeof notification === 'string' ? notification : '');
    return typeof text === 'string' ? text : JSON.stringify(text);
  }

  private getProjectId(item: any): string | undefined {
    const payload = this.getStructuredPayload(item);
    const projectId = this.findPayloadValue(
      [item, payload],
      ['project_Id', 'projectId', 'project_id', 'ProjectId', 'Project_ID', 'projectID']
    );
    if (projectId) {
      return String(projectId);
    }
    return undefined;
  }

  private getProjectName(item: any): string | undefined {
    const payload = this.getStructuredPayload(item);
    return this.findPayloadValue(
      [item, payload],
      ['project_Name', 'projectName', 'project_name']
    );
  }

  private getCustomerName(item: any): string | undefined {
    const payload = this.getStructuredPayload(item);
    return this.findPayloadValue(
      [item, payload],
      ['customer_Name', 'customerName', 'customer_name']
    );
  }

  private findPayloadValue(sources: any[], keys: string[]): string | undefined {
    const pending = sources.filter(source => source && typeof source === 'object');
    const visited = new Set<any>();
    while (pending.length) {
      const source = pending.shift();
      if (visited.has(source)) {
        continue;
      }
      visited.add(source);

      for (const key of keys) {
        const value = source[key];
        if (value !== undefined && value !== null && value !== '') {
          return String(value);
        }
      }

      for (const value of Object.values(source)) {
        if (value && typeof value === 'object') {
          pending.push(value);
        }
      }
    }
    return undefined;
  }
}
